/**
 * Hook for initializing filters in the Entitlement tab
 *
 * This hook handles the initialization of advanced filters for entitlement violations.
 * It fetches filter options from APIs, applies personalization settings, and configures filters.
 *
 * Key features:
 * - Asynchronous loading of filter options from APIs
 * - Error handling for filter initialization failures
 * - User personalization support for saved filters
 * - Localization of filter labels
 * - Integration with filter cache for performance optimization
 *
 * @file use-filterinitialization.ts
 * @module Entitlement/hooks
 */
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import { type MenuOption } from '@saviynt/design-system';

import {
  ADVANCED_FILTERS_METHODS,
  DEFAULT_AD_FILTER_PARAMS,
  ENTITLEMENT_INITIAL_FILTERS,
  PERSONALIZATION_DEFAULT_CONFIG,
} from '../../../shared/constants';
import { ENTITLEMENT_TAB_LIST_AD_FILTER } from '../../../shared/constants/entitlements';
import {
  useInvertedLocalize,
  usePersonalization,
  useTypedSelector,
} from '../../../shared/hooks';
import {
  type ActiveFiltersState,
  type AdvancedFilterState,
} from '../../../shared/types';
import { entitlementActions } from '../data-store/entitlementSlice';

import useFilterCache from './use-filtercache';
import useGetQueryMap from './use-getQueryMap';

/**
 * Interface representing a Promise that can be unwrapped
 * @template T The type of value the promise resolves to
 */
interface UnwrappablePromise<T> extends Promise<T> {
  unwrap(): Promise<T>;
}

/**
 * Type definition for the query map object
 * Maps filter keys to their corresponding query functions
 */
type QueryMapType = Record<
  string,
  (params: typeof DEFAULT_AD_FILTER_PARAMS) => UnwrappablePromise<unknown>
>;

/**
 * Interface representing a query item with its key, function and parameters
 */
interface QueryItem {
  key: string;
  fn: (params: typeof DEFAULT_AD_FILTER_PARAMS) => UnwrappablePromise<unknown>;
  params: typeof DEFAULT_AD_FILTER_PARAMS;
}

/**
 * Interface for mapping query responses to success and error states
 */
interface ResponseMap {
  success: Record<string, unknown>;
  errors: Record<string, unknown>;
}

/**
 * Custom hook for initializing and managing advanced filters
 * @returns {Object} Object containing the initializeAdvancedFilters function
 *
 * @example
 * const { initializeAdvancedFilters } = useFiltersInitialization(
 *   queryMap,
 *   translateFunction
 * );
 */
const useFiltersInitialization = () => {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();

  const { filterCache, filters } = useTypedSelector(
    (state) => state.aag.entitlement.controls
  );

  // Hooks
  const queryMap = useGetQueryMap(true) as QueryMapType;
  const { getPersonlization } = usePersonalization();
  const { setGroupInitialCache } = useFilterCache(
    filterCache,
    entitlementActions.setFiltersCache
  );

  // Constants
  const savedFilters = useMemo(() => {
    const personalization = getPersonlization();

    return PERSONALIZATION_DEFAULT_CONFIG.saveFilter &&
      personalization &&
      personalization.filters
      ? personalization.filters
      : null;
  }, [getPersonlization]);
  const initializeAdvancedFilters = useCallback(async (): Promise<boolean> => {
    const queryArr: QueryItem[] = Object.entries(queryMap).map(
      ([value, fn]) => ({
        key: value,
        fn,
        params: DEFAULT_AD_FILTER_PARAMS,
      })
    );

    try {
      const results = await Promise.allSettled(
        queryArr.map(({ params: params_2, fn }) => fn(params_2).unwrap())
      );
      // Mapping response to success and error
      const responseMap = results.reduce<ResponseMap>(
        (acc, result_1, index) => {
          const { key } = queryArr[index];

          if (result_1.status === 'fulfilled') {
            acc.success[key] = result_1.value;

            return acc;
          }

          acc.errors[key] = result_1.reason;

          return acc;
        },
        { success: {}, errors: {} }
      );

      // If any filter api fails then throw exception
      const erroredFilters = Object.keys(responseMap.errors);

      if (erroredFilters.length > 0) {
        throw Error(`Error in fetching filter(s) ${erroredFilters.join('')}`);
      }

      const cacheObjects: Record<string, MenuOption[]> = {};
      const savedObjects: ActiveFiltersState[] = [];

      // Configuring filters
      const initialFilters: AdvancedFilterState[] = Object.values(
        ENTITLEMENT_TAB_LIST_AD_FILTER
      ).map((filter) => {
        const options = [];
        let isDefaultFilter = Boolean(
          (filter.value &&
            ENTITLEMENT_INITIAL_FILTERS.some(
              (initialFilter) => initialFilter.value === filter.value
            )) ||
            filters.find((_filter) => _filter.value === filter.value)?.isChecked
        );
        const isOptionFromApi =
          filter.method === ADVANCED_FILTERS_METHODS.API_WITH_OPTIONS &&
          responseMap.success[filter.value];
        const isFixedOption =
          filter.method === ADVANCED_FILTERS_METHODS.CUSTOM ||
          filter.method === ADVANCED_FILTERS_METHODS.FIXED;
        const isPersonalized = Boolean(
          savedFilters !== null && Array.isArray(savedFilters[filter.value])
        );

        if (isOptionFromApi) {
          const successResponse = responseMap.success[
            filter.value
          ] as MenuOption[];

          options.push(...successResponse);

          cacheObjects[filter.value] = successResponse;
        }

        if (isFixedOption && filter.options) {
          const localizedOptions = filter.options.map((option) => ({
            ...option,
            label: l(option.label),
          }));

          options.push(...localizedOptions);
        }

        if (isPersonalized && savedFilters?.[filter.value]) {
          const savedOptions = savedFilters[filter.value];
          const optionMap = new Map(
            options.map((option) => [option.value, option])
          );

          savedOptions.forEach(([value, label]) => {
            const existing = optionMap.get(value);

            if (existing) {
              optionMap.set(value, { ...existing, isChecked: true });
            } else {
              optionMap.set(value, { label, value, isChecked: true });
            }
          });
          const activeOptions = Array.from(optionMap.values()).filter(
            (option) => option.isChecked
          );

          savedObjects.push({
            value: filter.value,
            method: filter.method,
            options: activeOptions,
          });

          isDefaultFilter = true;
        }

        return {
          label: l(filter.label),
          value: filter.value,
          isDisabled: false,
          isChecked: isDefaultFilter,
          method: filter.method,
          category: filter.category,
          options,
        };
      });

      // Collect all active filters (both personalized and default checked filters)
      const allActiveFilters = [...savedObjects];

      // Add default checked filters that have options
      initialFilters.forEach((filter) => {
        if (
          filter.isChecked &&
          filter.options &&
          filter.options.length > 0 &&
          !savedObjects.some((saved) => saved.value === filter.value)
        ) {
          // For filters with fixed options, add checked options to activeFilters
          const checkedOptions = filter.options.filter(
            (option) => option.isChecked
          );

          if (checkedOptions.length > 0) {
            allActiveFilters.push({
              value: filter.value,
              method: filter.method,
              options: checkedOptions,
            });
          }
        }
      });

      // Set active filters
      dispatch(entitlementActions.setActiveFilters(allActiveFilters));

      // Initial cache
      setGroupInitialCache(cacheObjects, DEFAULT_AD_FILTER_PARAMS.search);

      // Filters configuration
      dispatch(entitlementActions.setAdvancedFilters(initialFilters));

      return true;
    } catch (error) {
      Logger.log('Error fetching entitlement filters', error);

      return false;
    }
  }, [l, queryMap, filters, savedFilters, dispatch, setGroupInitialCache]);

  return { initializeAdvancedFilters };
};

// eslint-disable-next-line import/prefer-default-export
export default useFiltersInitialization;
